import { PayloadAction } from '@reduxjs/toolkit';
import { FeatureTypes } from '../enums/index';

export const isIPCNote = (note: any): note is CMxAPI.IPCNote => {
  return hasAllKeys(note, ['encoding', 'mime_type']);
};

export const isZippedCodeBlock = (
  codeBlock: any
): codeBlock is CMxCommonApp.ZippedCodeBlock => {
  return 'zipped' in codeBlock && codeBlock.zipped === true;
};

export const isChargeSessionCode = (
  codeBlock: any
): codeBlock is CMxCommonApp.ChargeSessionCode => {
  return !isZippedCodeBlock(codeBlock);
};

export const isNgram = (feature: any): feature is CMxAPI.NgramFeature => {
  return (
    feature &&
    `type` in feature &&
    feature.type.toLowerCase() === FeatureTypes.NGRAM.toLowerCase()
  );
};

export const isPrior = (feature: any): feature is CMxAPI.PriorFeature => {
  return (
    feature &&
    `type` in feature &&
    feature.type.toLowerCase() === FeatureTypes.PRIOR.toLowerCase()
  );
};

export const isCategorical = (
  feature: any
): feature is CMxAPI.CategoricalFeature => {
  return (
    feature &&
    `type` in feature &&
    !isPrior(feature) &&
    feature.type === FeatureTypes.CATEGORICAL
  );
};

export const isICD = (
  code: CMxAPI.MedicalCode
): code is CMxAPI.DiagnosisCode => {
  return 'diagnosis_code' in code;
};

export const isMedicalCode = (code: any): code is CMxAPI.MedicalCode => {
  return isCPT(code) || isICD(code) || isModifier(code);
};

export const isCPT = (
  code: CMxAPI.MedicalCode
): code is CMxAPI.ProcedureCode => {
  return 'procedure_code' in code;
};

export const isModifier = (
  code: CMxAPI.MedicalCode
): code is CMxAPI.ModifierCode => {
  return 'modifier_code' in code;
};

export const isEvidence = (evidence: any): evidence is CMxAPI.UnionEvidence => {
  return isUnknownEvidence(evidence) || isIndividualEvidence(evidence);
};

export const isUnknownEvidence = (
  evidence: CMxAPI.UnionEvidence
): evidence is CMxAPI.UnknownEvidence => {
  return (
    'type' in evidence &&
    (evidence.type || '').toLowerCase() === 'unknown' &&
    'details' in evidence
  );
};

export const isIndividualEvidence = (
  evidence: CMxAPI.UnionEvidence
): evidence is CMxAPI.IndividualEvidence => {
  return 'feature' in evidence && 'score' in evidence;
};

const hasAllKeys = (candidate: any, keys: string[]) => {
  const canidateKeys = Object.keys(candidate);
  if (keys.length > canidateKeys.length) {
    return false;
  }
  return keys.every(key => canidateKeys.indexOf(key) !== -1);
};

export const isSearchResult = (
  candidate: any
): candidate is CMxCommonApp.CodeInfo => {
  const keys = [
    'code',
    'codeType',
    'description',
    'shortDescription',
    'tenantDefined'
  ];
  return hasAllKeys(candidate, keys);
};

export const isOrganizationRelationship = (
  candidate: Partial<CMxAPI.OrganizationRelationship>
): candidate is CMxAPI.OrganizationRelationship => {
  const keys = [
    'createdDate',
    'effectiveDate',
    'endDate',
    'fromOrganizationId',
    'id',
    'organizationRelationshipType',
    'serviceLineId',
    'toOrganizationId',
    'updateDate',
    'version'
  ];
  const canidateKeys = Object.keys(candidate);
  if (keys.length > canidateKeys.length) {
    return false;
  }
  return keys.every(key => canidateKeys.indexOf(key) !== -1);
};

export const isCaseRunV2 = (
  candidate: CMxAPI.CaseRun | CMxAPI.CaseRunV2
): candidate is CMxAPI.CaseRunV2 => {
  return (
    `invoices` in candidate &&
    `version` in candidate &&
    candidate.version === '2.0'
  );
};

export const isCaseV2 = (
  candidate: CMxAPI.Case | CMxAPI.CaseV2
): candidate is CMxAPI.CaseV2 => {
  return candidate.caseRuns.every(isCaseRunV2);
};

export const isCaseV1 = (
  candidate: CMxAPI.Case | CMxAPI.CaseV2
): candidate is CMxAPI.CaseV2 => {
  return !isCaseV2(candidate);
};

export const isCaseRunV1 = (
  candidate: CMxAPI.CaseRun
): candidate is CMxAPI.CaseRun => {
  return !isCaseRunV2(candidate);
};

const isDiagnosisCodeDescription = (
  code: any
): code is InvoicesAPI.DiagnosticCodeDescription => {
  return 'diagnosis_code' in code;
};

const isInvoiceCode = (code: any) => {
  const missing = [
    'evidence',
    'confidence',
    'threashold',
    'automate',
    'used'
  ].filter(x => !(x in code));
  return missing.length === 0;
};

/**
 * Checks if the provided `action` object is a valid Redux action.
 * This guard expects an object with `type` and `payload` properties.
 *
 * @param action - The action object to check.
 * @returns `true` if the action is a valid Redux action, `false` otherwise.
 */
export const isReduxAction = (action: any): action is PayloadAction<any> => {
  if (typeof action !== 'object') return false;
  return 'type' in action && 'payload' in action;
};

export const InvoiceCodes = {
  isICD: (code: any): code is InvoicesAPI.DiagnosticCode => {
    if (!isInvoiceCode(code)) {
      return false;
    }
    return true;
  },
  isModifier: (code: any): code is InvoicesAPI.Modifier => {
    if (!isInvoiceCode(code)) {
      return false;
    }
    return true;
  },
  isTechCode: (code: any): code is InvoicesAPI.TechnicalCode => {
    const techKeysPresent = ['units', 'code', 'provenance'].every(
      x => x in code
    );
    return techKeysPresent;
  },
  isCPT: (code: any): code is InvoicesAPI.ProcedureCode => {
    if (!isInvoiceCode(code)) {
      return false;
    }
    return true;
  }
};
